import { useEffect, type ReactNode } from 'react'
import type { TFunction } from 'i18next'
import { TriangleAlert } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import type { SystemMetrics } from '@/api/types'
import { SYSTEM_SAMPLE_MS, useSystem } from '@/features/admin/queries'
import { byteSize } from '@/shared/lib/bytes'
import { Alert, AlertTitle } from '@/shared/ui/alert'
import { Progress } from '@/shared/ui/progress'
import { Skeleton } from '@/shared/ui/skeleton'
import { sampleOf, useHealthHistory } from '../health-history'
import { HealthCharts } from './health-charts'

type Workers = SystemMetrics['workers']
type Disk = SystemMetrics['disks'][number]
type Bucket = SystemMetrics['buckets'][number]

const COUNT = new Intl.NumberFormat()
const DECIMAL = new Intl.NumberFormat(undefined, { maximumFractionDigits: 2 })
const PERCENT = new Intl.NumberFormat(undefined, {
  style: 'percent',
  maximumFractionDigits: 0,
})
const COUNTED = new Intl.DateTimeFormat(undefined, { timeStyle: 'short' })

/** Past this a disk is worth a warning: Postgres stops writing at none. */
const NEARLY_FULL = 90

/**
 * What the host and the background workers are doing, and have been doing.
 *
 * Every answer is recorded as a point for the charts, so the record grows only
 * while this is mounted -- which is what the charts say about their gaps.
 */
export function SystemHealth() {
  const { t } = useTranslation()
  const system = useSystem()
  const samples = useHealthHistory((state) => state.samples)
  const record = useHealthHistory((state) => state.record)

  useEffect(() => {
    if (system.data !== undefined) record(sampleOf(system.data))
  }, [system.data, record])

  if (system.isPending) return <Skeleton className="h-64 w-full" />

  if (system.data === undefined) {
    return (
      <Alert variant="destructive">
        <AlertTitle>{t('admin.systemUnavailable')}</AlertTitle>
      </Alert>
    )
  }

  const metrics = system.data

  return (
    <>
      <Resources metrics={metrics} />
      <HealthCharts samples={samples} interval={SYSTEM_SAMPLE_MS} />
      <WorkerList workers={metrics.workers} sampledAt={metrics.sampled_at} />
      <Storage metrics={metrics} />
    </>
  )
}

function Resources({ metrics }: { metrics: SystemMetrics }) {
  const { t } = useTranslation()
  const { cpu, memory } = metrics

  return (
    <section className="space-y-3">
      <div>
        <h2 className="font-medium">{t('admin.system')}</h2>
        <p className="text-muted-foreground text-sm">{t('admin.systemWhy')}</p>
      </div>
      <div className="grid gap-3 sm:grid-cols-3">
        <Tile
          label={t('admin.cpu')}
          value={PERCENT.format(cpu.percent / 100)}
          percent={cpu.percent}
          detail={t('admin.cpuDetail', {
            count: cpu.count,
            load: cpu.load_average.map((load) => DECIMAL.format(load)).join(' · '),
          })}
        />
        <Tile
          label={t('admin.memory')}
          value={PERCENT.format(memory.percent / 100)}
          percent={memory.percent}
          detail={t('admin.ofTotal', {
            used: byteSize(memory.used_bytes),
            total: byteSize(memory.total_bytes),
          })}
        />
        <Tile
          label={t('admin.uptime')}
          value={duration(metrics.uptime_seconds, t)}
          detail={t('admin.uptimeWhy')}
        />
      </div>
    </section>
  )
}

/** One headline figure, with a bar when it is a share of something. */
function Tile({
  label,
  value,
  detail,
  percent,
}: {
  label: string
  value: string
  detail: ReactNode
  percent?: number
}) {
  return (
    <div className="space-y-2 rounded-lg border p-4 bg-background on-surface">
      <p className="text-muted-foreground text-sm">{label}</p>
      <p className="text-2xl font-semibold tabular-nums">{value}</p>
      {percent !== undefined && <Progress value={percent} aria-label={label} />}
      <p className="text-muted-foreground text-xs tabular-nums">{detail}</p>
    </div>
  )
}

/**
 * Every worker that answered, what it consumes and what it is running, and how
 * much is waiting in each queue.
 */
function WorkerList({ workers, sampledAt }: { workers: Workers; sampledAt: string }) {
  const { t } = useTranslation()
  const now = Date.parse(sampledAt)
  const busy = workers.workers.reduce((sum, worker) => sum + worker.busy, 0)
  const slots = workers.workers.reduce((sum, worker) => sum + worker.concurrency, 0)

  return (
    <section className="space-y-3 rounded-lg border p-4 bg-background on-surface">
      <div className="flex items-center justify-between gap-3">
        <h2 className="font-medium">{t('admin.workers')}</h2>
        {workers.workers.length > 0 && (
          <span className="text-muted-foreground text-sm tabular-nums">
            {t('admin.workersSummary', { busy, slots })}
          </span>
        )}
      </div>

      {!workers.reachable ? (
        <p className="text-destructive text-sm">{t('admin.workersUnreachable')}</p>
      ) : workers.workers.length === 0 ? (
        <p className="text-destructive text-sm">{t('admin.workersNone')}</p>
      ) : (
        <ul className="divide-y">
          {workers.workers.map((worker) => (
            <li key={worker.name} className="space-y-2 py-3">
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <p className="truncate font-mono text-sm">{worker.name}</p>
                  <p className="text-muted-foreground text-xs">
                    {worker.queues.join(', ')}
                  </p>
                </div>
                <div className="shrink-0 text-right text-sm tabular-nums">
                  <p>
                    {t('admin.workerBusy', {
                      busy: worker.busy,
                      slots: worker.concurrency,
                    })}
                  </p>
                  <p className="text-muted-foreground text-xs">
                    {t('admin.workerProcessed', {
                      count: worker.processed,
                      formatted: COUNT.format(worker.processed),
                    })}
                  </p>
                </div>
              </div>
              <Progress
                value={
                  worker.concurrency === 0 ? 0 : (worker.busy / worker.concurrency) * 100
                }
                aria-label={worker.name}
              />
              {worker.running.length > 0 && (
                <ul className="space-y-1">
                  {worker.running.map((task, index) => (
                    <li
                      key={`${task.name}-${String(index)}`}
                      className="flex justify-between gap-4 text-xs"
                    >
                      <span className="truncate font-mono" title={task.name}>
                        {task.name.split('.').pop()}
                      </span>
                      {task.started_at !== null && (
                        <span className="text-muted-foreground shrink-0 tabular-nums">
                          {duration((now - Date.parse(task.started_at)) / 1000, t)}
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </li>
          ))}
        </ul>
      )}

      {workers.queues.length > 0 && (
        <div className="space-y-1">
          <h3 className="text-sm font-medium">{t('admin.queues')}</h3>
          <ul className="divide-y">
            {workers.queues.map((queue) => (
              <li key={queue.name} className="flex justify-between gap-4 py-2 text-sm">
                <span className="font-mono">{queue.name}</span>
                <span className="tabular-nums">
                  {t('admin.queueWaiting', { count: queue.size })}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  )
}

function Storage({ metrics }: { metrics: SystemMetrics }) {
  const { t } = useTranslation()

  return (
    <section className="space-y-3 rounded-lg border p-4 bg-background on-surface">
      <h2 className="font-medium">{t('admin.storage')}</h2>
      <ul className="divide-y">
        {metrics.disks.map((disk) => (
          <DiskRow key={disk.name} disk={disk} />
        ))}
        <li className="flex items-center justify-between gap-4 py-2">
          <span className="text-sm">{t('admin.databaseSize')}</span>
          <span className="text-sm tabular-nums">
            {byteSize(metrics.database_size_bytes)}
          </span>
        </li>
        {metrics.buckets.map((bucket) => (
          <BucketRow key={bucket.purpose} bucket={bucket} />
        ))}
      </ul>
    </section>
  )
}

function DiskRow({ disk }: { disk: Disk }) {
  const { t } = useTranslation()
  const label = disk.name === 'data' ? t('admin.diskData') : t('admin.diskSystem')

  return (
    <li className="space-y-2 py-2">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-sm">{label}</p>
          <p className="text-muted-foreground font-mono text-xs">{disk.path}</p>
        </div>
        <div className="text-right text-sm tabular-nums">
          <p>
            {t('admin.ofTotal', {
              used: byteSize(disk.used_bytes),
              total: byteSize(disk.total_bytes),
            })}
          </p>
          {disk.percent >= NEARLY_FULL ? (
            <p className="text-destructive flex items-center justify-end gap-1 text-xs">
              <TriangleAlert className="size-3" />
              {t('admin.diskNearlyFull', { free: byteSize(disk.free_bytes) })}
            </p>
          ) : (
            <p className="text-muted-foreground text-xs">
              {t('admin.diskFree', { free: byteSize(disk.free_bytes) })}
            </p>
          )}
        </div>
      </div>
      <Progress value={disk.percent} aria-label={label} />
    </li>
  )
}

function BucketRow({ bucket }: { bucket: Bucket }) {
  const { t } = useTranslation()
  const label =
    bucket.purpose === 'files' ? t('admin.bucketFiles') : t('admin.bucketExports')

  return (
    <li className="flex items-start justify-between gap-4 py-2">
      <div>
        <p className="text-sm">{label}</p>
        <p className="text-muted-foreground font-mono text-xs">{bucket.name}</p>
      </div>
      {bucket.error !== null ? (
        <span className="text-destructive text-right text-sm">{bucket.error}</span>
      ) : (
        <div className="text-right text-sm tabular-nums">
          <p>{byteSize(bucket.size_bytes ?? 0)}</p>
          <p className="text-muted-foreground text-xs">
            {t('admin.bucketObjects', {
              count: bucket.object_count ?? 0,
              formatted: COUNT.format(bucket.object_count ?? 0),
              time: COUNTED.format(new Date(bucket.counted_at)),
            })}
          </p>
        </div>
      )}
    </li>
  )
}

/** `3d 4h`, `4h 12m`, `12m` or `40s`: the largest units that say anything. */
function duration(seconds: number, t: TFunction): string {
  const minutes = Math.floor(seconds / 60)
  const hours = Math.floor(minutes / 60)
  const days = Math.floor(hours / 24)
  if (days > 0) return t('admin.durationDays', { days, hours: hours % 24 })
  if (hours > 0) return t('admin.durationHours', { hours, minutes: minutes % 60 })
  if (minutes > 0) return t('admin.durationMinutes', { minutes })
  return t('admin.durationSeconds', { seconds: Math.max(0, Math.floor(seconds)) })
}
